import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Artist } from '../types/artist';
import { Setlist } from '../types/setlist';
import { PredictedSetlist as PredictedSetlistType, PredictedSetlistSong } from '../types/predictedSetlist';
import { ArtistSearch } from '../components/ArtistSearch';
import { PredictedSetlist } from '../components/PredictedSetlist';
import { SetlistDisplay } from '../components/SetlistDisplay';
import { CreatePlaylistPanel } from '../components/CreatePlaylistPanel';
import { getArtistSetlists, getPredictedSetlist } from '../services/artistApi';
import { fetchProfile, logout } from '../services/api';

const MOBILE_BREAKPOINT = 768;

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [setlists, setSetlists] = useState<Setlist[]>([]);
  const [prediction, setPrediction] = useState<PredictedSetlistType | null>(null);
  const [orderedSongs, setOrderedSongs] = useState<PredictedSetlistSong[]>([]);
  const [selectedSongs, setSelectedSongs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setlistCount, setSetlistCount] = useState(5);
  const [excludedSetlistIds, setExcludedSetlistIds] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [openPanels, setOpenPanels] = useState<Set<string>>(new Set(['search', 'setlist', 'playlist']));
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const togglePanel = (panel: string) => {
    setOpenPanels(prev => {
      const next = new Set(prev);
      next.has(panel) ? next.delete(panel) : next.add(panel);
      return next;
    });
  };

  useEffect(() => {
    fetchProfile()
      .then(() => setAuthChecked(true))
      .catch(err => {
        if (err.message === 'TOKEN_EXPIRED' || err.message === 'UNAUTHENTICATED') {
          navigate('/login');
        } else {
          setAuthChecked(true);
        }
      });
  }, [navigate]);

  const loadSetlistsForArtist = async (artist: Artist, count: number) => {
    setSetlists([]);
    setPrediction(null);
    setOrderedSongs([]);
    setSelectedSongs(new Set());
    setExcludedSetlistIds(new Set());
    setError(null);
    setLoading(true);

    try {
      const [setlistData, predictionData] = await Promise.all([
        getArtistSetlists(artist.id, count),
        getPredictedSetlist(artist.id, count),
      ]);
      setSetlists(setlistData.setlists);
      setPrediction(predictionData);
      setOrderedSongs(predictionData.songs);
      setSelectedSongs(new Set(predictionData.songs.map(s => s.name)));
    } catch (err: any) {
      setError(err.message || 'Failed to load artist data');
    } finally {
      setLoading(false);
    }
  };

  const handleArtistSelect = async (artist: Artist) => {
    setSelectedArtist(artist);
    await loadSetlistsForArtist(artist, setlistCount);
  };

  const handleSetlistCountReload = async () => {
    if (!selectedArtist) return;
    await loadSetlistsForArtist(selectedArtist, setlistCount);
  };

  const handleOrderChange = (songs: PredictedSetlistSong[]) => {
    setOrderedSongs(songs);
    if (prediction) {
      setPrediction({ ...prediction, songs });
    }
  };

  const handleToggleExclude = async (id: string) => {
    if (!selectedArtist) return;
    const newExcluded = new Set(excludedSetlistIds);
    if (newExcluded.has(id)) {
      newExcluded.delete(id);
    } else {
      newExcluded.add(id);
    }
    setExcludedSetlistIds(newExcluded);
    try {
      const excludeArray = Array.from(newExcluded);
      const predictionData = await getPredictedSetlist(selectedArtist.id, setlistCount, excludeArray);
      setPrediction(predictionData);
      setOrderedSongs(predictionData.songs);
      setSelectedSongs(new Set(predictionData.songs.map(s => s.name)));
    } catch (err: any) {
      setError(err.message || 'Failed to update prediction');
    }
  };

  const handleLogout = async () => {
    await logout().catch(() => {});
    navigate('/login');
  };

  if (!authChecked) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading…</div>;
  }

  const header = (
    <header style={{ background: '#191414', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <h1 style={{ margin: 0, fontSize: '20px', color: '#1DB954' }}>Spotlist</h1>
        <span style={{ color: '#aaa', fontSize: '14px' }}>Predicted Setlist Creator</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          onClick={() => setInfoOpen(true)}
          aria-label="About Spotlist"
          title="About Spotlist"
          style={{
            background: 'transparent',
            border: '1px solid #aaa',
            borderRadius: '50%',
            color: '#aaa',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 700,
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          ℹ
        </button>
        <button
          onClick={handleLogout}
          style={{
            background: 'transparent',
            border: '1px solid #aaa',
            borderRadius: '4px',
            color: '#aaa',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '6px 14px',
          }}
        >
          Log out
        </button>
      </div>
    </header>
  );

  const infoModal = infoOpen && (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="About Spotlist"
      onClick={() => setInfoOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#282828',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '520px',
          width: '90%',
          color: '#e0e0e0',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          position: 'relative',
        }}
      >
        <button
          onClick={() => setInfoOpen(false)}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            color: '#aaa',
            fontSize: '22px',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ×
        </button>
        <h2 style={{ margin: '0 0 6px', fontSize: '22px', color: '#1DB954' }}>About Spotlist</h2>
        <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Predicted Setlist Creator
        </p>
        <p style={{ margin: '0 0 16px', fontSize: '15px', lineHeight: 1.65 }}>
          Spotlist analyses recent concert setlists for any artist and predicts what songs they're likely to play at their next show. It looks at how frequently each track has appeared across a chosen number of recent setlists and ranks them accordingly.
        </p>
        <p style={{ margin: '0 0 16px', fontSize: '15px', lineHeight: 1.65 }}>
          To get started, search for an artist using the search panel, then choose how many recent setlists to analyse. Spotlist will display the predicted setlist in the centre panel — you can drag songs to reorder them and tick or untick songs to include or exclude them.
        </p>
        <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.65 }}>
          Once you're happy with your selection, use the <strong style={{ color: '#1DB954' }}>Create Playlist</strong> panel to save it straight to your Spotify library, ready to listen to before the concert.
        </p>
      </div>
    </div>
  );

  const searchPanelContent = (
    <>
      <ArtistSearch onArtistSelect={handleArtistSelect} />
      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label htmlFor="setlist-count" style={{ fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>
          Setlists to analyse:
        </label>
        <input
          id="setlist-count"
          type="number"
          min={1}
          max={100}
          value={setlistCount}
          onChange={e => {
            const val = Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 1));
            setSetlistCount(val);
          }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSetlistCountReload(); } }}
          style={{
            width: '64px',
            padding: '4px 6px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '13px',
          }}
        />
        <button
          onClick={handleSetlistCountReload}
          aria-label="Search setlists"
          style={{
            padding: '4px 10px',
            background: '#1DB954',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          Search
        </button>
      </div>
      {error && (
        <div style={{ marginTop: '16px', color: '#c00', fontSize: '14px' }} role="alert">{error}</div>
      )}
      {loading && (
        <div style={{ marginTop: '16px', color: '#888', textAlign: 'center' }} role="status" aria-live="polite">
          Loading…
        </div>
      )}
      {selectedArtist && (
        <div style={{ marginTop: '24px' }}>
          <SetlistDisplay
            setlists={setlists}
            artistName={selectedArtist.name}
            excludedIds={excludedSetlistIds}
            onToggleExclude={handleToggleExclude}
          />
        </div>
      )}
    </>
  );

  const hasIncludedSetlists = setlists.length > 0 && setlists.some(s => !excludedSetlistIds.has(s.id));

  const setlistPanelContent = prediction && hasIncludedSetlists ? (
    <PredictedSetlist
      prediction={{ ...prediction, songs: orderedSongs }}
      selectedSongs={selectedSongs}
      onSelectionChange={setSelectedSongs}
      onOrderChange={handleOrderChange}
    />
  ) : (
    <div style={{ color: '#aaa', textAlign: 'center', marginTop: '40px' }}>
      Search for an artist to see their predicted setlist.
    </div>
  );

  const playlistPanelContent = prediction && hasIncludedSetlists ? (
    <CreatePlaylistPanel
      artistName={prediction.artistName}
      selectedSongs={orderedSongs.filter(s => selectedSongs.has(s.name)).map(s => s.name)}
    />
  ) : (
    <div style={{ color: '#aaa', textAlign: 'center', marginTop: '40px' }}>Create Playlist will appear here.</div>
  );

  if (isMobile) {
    const mobilePanels = [
      { key: 'search', label: 'Search & Setlists', content: searchPanelContent },
      { key: 'setlist', label: 'Predicted Setlist', content: setlistPanelContent },
      { key: 'playlist', label: 'Create Playlist', content: playlistPanelContent },
    ];

    return (
      <div style={{ minHeight: '100vh', background: '#fafafa' }}>
        {infoModal}
        {header}
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {mobilePanels.map(({ key, label, content }) => {
            const isOpen = openPanels.has(key);
            return (
              <div key={key} style={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                <button
                  onClick={() => togglePanel(key)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 16px',
                    background: '#f7f7f7',
                    border: 'none',
                    borderBottom: isOpen ? '1px solid #eee' : 'none',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#191414',
                    textAlign: 'left',
                  }}
                >
                  {label}
                  <span style={{ color: '#1DB954', fontSize: '18px', lineHeight: 1 }}>{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && (
                  <div style={{ padding: '16px' }}>
                    {content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa' }}>
      {infoModal}
      {header}
      <div style={{ display: 'flex', gap: '16px', padding: '24px', height: 'calc(100vh - 64px)', boxSizing: 'border-box' }}>
        {/* Left panel: Artist Search + Recent Setlists */}
        <div style={{ width: '300px', flexShrink: 0, overflowY: 'auto' }}>
          {searchPanelContent}
        </div>

        {/* Middle panel: Predicted Setlist */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#fff', borderRadius: '8px', padding: '16px', border: '1px solid #eee' }}>
          {setlistPanelContent}
        </div>

        {/* Right panel: Create Playlist */}
        <div style={{ width: '360px', flexShrink: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {playlistPanelContent}
        </div>
      </div>
    </div>
  );
};

export default MainPage;
